<template>
  <div class="DoctorNotes">
    <v-card outlined>
      <v-card-title class="subtitle-1">Anotações Clínicas</v-card-title>
      <v-card-text>
        <v-textarea
          v-model="newNote"
          label="Nova anotação"
          rows="3"
          outlined
          dense
          hide-details
        />
        <div class="d-flex align-center mt-2" style="gap:8px">
          <v-checkbox
            v-model="showInChart"
            label="Exibir no gráfico"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!newNote.trim() || saving"
            :loading="saving"
            small
            @click="saveNote"
          >
            Salvar anotação
          </v-btn>
        </div>
      </v-card-text>

      <v-divider v-if="notes.length" />

      <v-list dense v-if="notes.length">
        <template v-for="note in notes">
          <!-- Edit mode -->
          <v-list-item v-if="editingNoteId === note.id" :key="`edit-${note.id}`" class="align-start py-2">
            <v-list-item-content>
              <v-textarea
                v-model="editingText"
                rows="3"
                outlined
                dense
                hide-details
                auto-grow
              />
              <div class="d-flex align-center mt-1" style="gap:8px">
                <v-text-field
                  v-model="editingCreatedAt"
                  type="datetime-local"
                  label="Data da anotação"
                  outlined
                  dense
                  hide-details
                  class="mb-2"
                />
                <v-checkbox
                  v-model="editingShowInChart"
                  label="Exibir no gráfico"
                  hide-details
                  dense
                  class="mt-0 pt-0"
                />
                <v-spacer />
                <v-btn text small @click="cancelEdit">Cancelar</v-btn>
                <v-btn
                  color="primary"
                  small
                  :disabled="!editingText.trim() || updating"
                  :loading="updating"
                  @click="updateNote(note.id)"
                >
                  Salvar
                </v-btn>
              </div>
            </v-list-item-content>
          </v-list-item>

          <!-- Read mode -->
          <v-list-item v-else :key="note.id" class="align-start">
            <v-list-item-content>
              <v-list-item-subtitle class="caption">{{ formatDate(note.created_at) }}</v-list-item-subtitle>
              <v-list-item-title class="body-2 text-wrap">{{ note.note }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center" style="gap:4px">
              <v-icon v-if="note.show_in_chart" small color="primary" title="Exibido no gráfico">mdi-chart-line</v-icon>
              <v-btn icon small @click="startEdit(note)">
                <v-icon small color="grey">mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon small @click="deleteNote(note.id)">
                <v-icon small color="grey">mdi-delete-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
    </v-card>
  </div>
</template>

<script>
import { PatientService } from '../../lib/spasms-control'
import { getToken } from '../../utils'
import store from '../../store'

export default {
  name: 'DoctorNotes',
  props: {
    dependentId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      notes: [],
      newNote: '',
      showInChart: false,
      saving: false,
      editingNoteId: null,
      editingText: '',
      editingShowInChart: false,
      editingCreatedAt: '',
      updating: false
    }
  },
  methods: {
    loadNotes() {
      const service = new PatientService(getToken())
      service.listNotes(this.dependentId).then(response => {
        this.notes = response.data
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
      })
    },
    saveNote() {
      this.saving = true
      const service = new PatientService(getToken())
      service.createNote(this.dependentId, this.newNote.trim(), this.showInChart).then(note => {
        this.notes.unshift(note)
        this.newNote = ''
        this.showInChart = false
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
        store.setSnackBarMessageAction('Erro ao salvar anotação')
        store.setShowSnackBarAction(true)
      }).finally(() => {
        this.saving = false
      })
    },
    toDatetimeLocal(isoStr) {
      const d = new Date(isoStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    startEdit(note) {
      this.editingNoteId = note.id
      this.editingText = note.note
      this.editingShowInChart = note.show_in_chart
      this.editingCreatedAt = this.toDatetimeLocal(note.created_at)
    },
    cancelEdit() {
      this.editingNoteId = null
      this.editingText = ''
      this.editingShowInChart = false
      this.editingCreatedAt = ''
    },
    updateNote(noteId) {
      this.updating = true
      const service = new PatientService(getToken())
      const createdAtIso = new Date(this.editingCreatedAt).toISOString()
      service.updateNote(noteId, this.editingText.trim(), this.editingShowInChart, createdAtIso).then(updated => {
        const idx = this.notes.findIndex(n => n.id === noteId)
        if (idx !== -1) this.$set(this.notes, idx, updated)
        this.cancelEdit()
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
        store.setSnackBarMessageAction('Erro ao atualizar anotação')
        store.setShowSnackBarAction(true)
      }).finally(() => {
        this.updating = false
      })
    },
    deleteNote(noteId) {
      const service = new PatientService(getToken())
      service.deleteNote(noteId).then(() => {
        this.notes = this.notes.filter(n => n.id !== noteId)
        this.emitAnnotations()
      }).catch(err => {
        console.error(err)
        store.setSnackBarMessageAction('Erro ao remover anotação')
        store.setShowSnackBarAction(true)
      })
    },
    emitAnnotations() {
      this.$emit('annotationsChanged', this.notes.filter(n => n.show_in_chart))
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleString('pt-BR')
    }
  },
  created() {
    this.loadNotes()
  }
}
</script>
